import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { getStartEndDate, isDateInFuture, isPaDateTooFarInFuture } from './reportHandlers';
import { REPORT_TYPES } from '../constants';

export const MasterReportDialog = ({ open, onClose, onConfirm, months }: any) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dates, setDates] = useState({
    start: '',
    end: '',
    month: '',
    year: new Date().getFullYear(),
    week: '',
  });

  useEffect(() => {
    if (dates.week && dates.month && dates.year) {
      const { start, end } = getStartEndDate(REPORT_TYPES.FULL_CLAIMS, dates.week, dates.month, dates.year);
      setDates((prev) => ({ ...prev, start, end }));
    }
  }, [dates.week, dates.month, dates.year]);

  useEffect(() => {
    if (!open) {
      setDates({
        start: '',
        end: '',
        month: '',
        year: new Date().getFullYear(),
        week: '',
      });
    }
  }, [open]);

  const currentMonthIndex = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const selectedMonthIndex = months.indexOf(dates.month);

  const isFutureDate =
    dates.month !== '' &&
    (dates.year > currentYear || (dates.year === currentYear && selectedMonthIndex > currentMonthIndex));

  const isInvalid = !dates.week || !dates.month || isFutureDate || isSubmitting;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(dates);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Master Report Generation</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl>
              <InputLabel>Select Week</InputLabel>
              <Select
                value={dates.week}
                label="Select Week"
                onChange={(e) => setDates({ ...dates, week: e.target.value })}
              >
                {['Week 1', 'Week 2', 'Week 3', 'Week 4'].map((w) => (
                  <MenuItem key={w} value={w}>
                    {w}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth error={isFutureDate}>
              <InputLabel>Month</InputLabel>
              <Select value={dates.month} label="Month" onChange={(e) => setDates({ ...dates, month: e.target.value })}>
                {months.map((m: string) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
              {isFutureDate && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  Future billing months not allowed
                </Typography>
              )}
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={dates.year}
                label="Year"
                onChange={(e) => setDates({ ...dates, year: Number(e.target.value) })}
              >
                {[0, 1, 2, 3, 4].map((off) => (
                  <MenuItem key={off} value={new Date().getFullYear() - off}>
                    {new Date().getFullYear() - off}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Date Range: {dates.start || '---'} to {dates.end || '---'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={isInvalid}>
          {isSubmitting ? 'Submitting...' : 'Confirm & Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
